import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { detectPreferredCurrency, getSupportedCurrencies } from '../utils/currency';

const CurrencyContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [viewerCurrency, setViewerCurrency] = useState('USD');
  const [initialized, setInitialized] = useState(false);

  // Detect and set preferred currency
  useEffect(() => {
    const detectedCurrency = detectPreferredCurrency({
      userProfile: user,
      phoneCountryCode: user?.phone_country_code?.replace('+', ''),
      browserLocale: navigator.language,
    });
    
    setViewerCurrency(detectedCurrency);
    setInitialized(true);
  }, [user]);

  // Update currency preference on server
  const updatePreferredCurrency = async (currency) => {
    setViewerCurrency(currency);
    
    // Save to localStorage for persistence
    try {
      localStorage.setItem('roomhora_preferred_currency', currency);
    } catch (e) {
      // Ignore storage errors
    }
    
    // If user is logged in, save to profile
    if (user) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/api/auth/profile`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ preferred_currency: currency }),
        });
      } catch (e) {
        // Keep console.error for production error tracking
      }
    }
  };

  // Load from localStorage on mount
  useEffect(() => {
    if (!user?.preferred_currency) {
      try {
        const stored = localStorage.getItem('roomhora_preferred_currency');
        if (stored) {
          setViewerCurrency(stored);
        }
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [user]);

  return (
    <CurrencyContext.Provider value={{
      viewerCurrency,
      setViewerCurrency: updatePreferredCurrency,
      supportedCurrencies: getSupportedCurrencies(),
      initialized,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
